
import React, { useState, useEffect } from 'react';
import { ref, onValue, off, remove } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../firebase';
import { Edit3, Trash2 } from 'lucide-react';

const baseButton = {
  padding: '0.875rem 2rem',
  borderRadius: '8px',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer',
  border: 'none',
  fontSize: '0.95rem',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
  transform: 'translateY(0)',
};

const PartnerTable = ({ onAddPartner, onEditPartner }) => {
  const [partners, setPartners] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setPartners([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const partnersRef = ref(database, `users/${encodedEmail}/partners`);

    const handleData = (snapshot) => {
      const partnersData = [];
      snapshot.forEach((childSnapshot) => {
        partnersData.push({
          id: childSnapshot.key,
          ...childSnapshot.val(),
        });
      });
      setPartners(partnersData);
      setLoading(false);
    };

    const handleError = (error) => {
      console.error("Error fetching partners:", error);
      setLoading(false);
    };

    onValue(partnersRef, handleData, handleError);

    return () => {
      off(partnersRef, 'value', handleData);
    };
  }, [currentUser]);

  const handleDeletePartner = async (partnerId) => {
    if (!currentUser || !partnerId) return;

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const partnerRef = ref(database, `users/${encodedEmail}/partners/${partnerId}`);
      
      if (window.confirm('Are you sure you want to delete this partner?')) {
        await remove(partnerRef);
      }
    } catch (error) {
      console.error("Error deleting partner:", error);
    }
  };

  const exportToCSV = () => {
    const headers = ['S.No', 'Partner Name', 'Company Name', 'Address', 'Email', 'Mobile No', 'TaxNo'];
    const rows = partners.map((partner, index) => [
      index + 1,
      partner.name || 'N/A',
      partner.companyName || 'N/A',
      partner.address || 'N/A',
      partner.email || 'N/A',
      partner.mobileNo || 'N/A',
      `${partner.taxNo1 || 'N/A'} - ${partner.taxNo2 || 'N/A'}`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'partners_data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const filteredPartners = partners.filter(partner =>
    (partner.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (partner.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (partner.address?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (partner.email?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (partner.mobileNo?.includes(searchTerm) || false) ||
    (partner.taxNo1?.includes(searchTerm) || false) ||
    (partner.taxNo2?.includes(searchTerm) || false)
  );

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',

      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: {
      ...baseButton,
      backgroundColor: '#2563eb',
    },
    exportButton: {
      ...baseButton,
      backgroundColor: '#4caf50',
      marginRight: '1rem',
    },
    searchStyle: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '0.95rem',
      width: '200px',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
     
      textAlign: 'center',
      width: '60px',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
          <h2>Please login to access the Partners</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Partners</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
       
          <button
            onClick={onAddPartner}
            style={styles.addButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
            }}
          >
            + Add New Partner
          </button>
          <button
            onClick={exportToCSV}
            style={styles.exportButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(76, 175, 80, 0.3)';
            }}
          >
            Export CSV
          </button>
          <input
            type="text"
            placeholder="Search By Partner Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchStyle}
          />
        </div>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.noData}>
            <p>⏳ Loading partners...</p>
          </div>
        ) : filteredPartners.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>S.No</th>
                <th style={styles.tableHeader}>Partner Name</th>
                <th style={styles.tableHeader}>Company Name</th>
                <th style={styles.tableHeader}>Address</th>
                <th style={styles.tableHeader}>Email</th>
                <th style={styles.tableHeader}>Mobile No</th>
                <th style={styles.tableHeader}>TaxNo</th>
                <th style={styles.tableHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartners.map((partner, index) => (
                <tr
                  key={partner.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                  <td style={styles.tableCell}>{partner.name || 'N/A'}</td>
                  <td style={styles.tableCell}>{partner.companyName || 'N/A'}</td>
                  <td style={styles.tableCell}>{partner.address || 'N/A'}</td>
                  <td style={styles.tableCell}>{partner.email || 'N/A'}</td>
                  <td style={styles.tableCell}>{partner.mobileNo || 'N/A'}</td>
                  <td style={styles.tableCell}>{partner.taxNo1 || 'N/A'} - {partner.taxNo2 || 'N/A'}</td>
                  <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                    <button
                      onClick={() => onEditPartner(partner)}
                      style={styles.editButton}
                      title="Edit Partner"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                    ✏️

                    </button>
                    <button
                      onClick={() => handleDeletePartner(partner.id)}
                      style={styles.deleteButton}
                      title="Delete Partner"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                      }}
                    >
                    🗑️

                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.noData}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📁</div>
            <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Partners Found</h3>
            <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first partner!</p>
            <button
              onClick={onAddPartner}
              style={{
                ...styles.addButton,
                fontSize: '1rem',
                padding: '1rem 2rem',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Your First Partner
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerTable;